import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import ReactStars from 'react-rating-stars-component';
import { toast } from 'react-toastify';
import styled from 'styled-components';
import SettingsOutlinedIcon from '@mui/icons-material/Settings';
import AddPhotoAlternateOutlinedIcon from '@mui/icons-material/AddPhotoAlternateOutlined';

import FirebaseImage from '../Media/FirebaseImage';
import PBMark from '../Brand/PBMark';
import TrustPassport from '../Trust/TrustPassport';
import { getProfileImage, getProfileName } from '../../Utils/legacyData';
import { uploadProfileImage } from '../../Utils/uploads';
import { useI18n } from '../../i18n/I18nProvider';

const ProfileSidebar = ({ profile = {}, editPath, details = [], showRating = false }) => {
  const history = useHistory();
  const { t } = useI18n();
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);

  const fullName = getProfileName(profile);
  const profileImage = getProfileImage(profile);
  const rating = Number(profile.average_rating || profile.trust?.average_rating || profile.artisan_rating || profile.client_rating) || 0;
  const role = String(profile.user_type || '').toLowerCase() === 'artisan'
    ? t('common.professional')
    : String(profile.user_type || '').toLowerCase() === 'client'
      ? t('common.client')
      : t('common.member');

  const handleImage = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || uploading) return;

    setUploading(true);
    setProgress(0);
    try {
      await uploadProfileImage(file, setProgress);
      toast.success(t('profileSidebar.photoUpdated'));
    } catch (error) {
      toast.error(error?.message || t('profileSidebar.photoError'));
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  return (
    <Aside>
      <Cover>
        <PBMark size="3rem" />
      </Cover>
      <Body>
        <AvatarWrap>
          <Avatar src={profileImage} fallbackSrc="/images/user.svg" alt={t('profileSidebar.profileOf', { name: fullName })} />
          <UploadLabel aria-disabled={uploading} title={t('profileSidebar.changePhoto')}>
            <input type="file" accept="image/jpeg,image/png,image/webp" onChange={handleImage} disabled={uploading} />
            <AddPhotoAlternateOutlinedIcon />
          </UploadLabel>
        </AvatarWrap>
        <Name>{fullName}</Name>
        <Role>{role}</Role>
        {uploading && <Progress>{t('profileSidebar.uploading', { progress })}</Progress>}

        {showRating && (
          <RatingCard>
            <RatingLabel>{t('profileSidebar.averageRating')}</RatingLabel>
            {rating > 0 ? (
              <RatingLine>
                <ReactStars count={5} value={rating} size={17} isHalf edit={false} activeColor="#c0a375" />
                <strong>{rating.toFixed(1)}</strong>
              </RatingLine>
            ) : (
              <Muted>{t('profileSidebar.noRating')}</Muted>
            )}
          </RatingCard>
        )}

        {showRating && <TrustPassport profile={profile} role={profile.user_type} compact />}

        {details.length > 0 && (
          <Details>
            {details.map((detail) => {
              const content = (
                <>
                  <span>{detail.label}</span>
                  <strong>{detail.value || t('common.notSpecified')}</strong>
                </>
              );
              return detail.onClick ? (
                <DetailButton key={detail.label} type="button" onClick={detail.onClick}>{content}</DetailButton>
              ) : (
                <Detail key={detail.label}>{content}</Detail>
              );
            })}
          </Details>
        )}

        {editPath && (
          <EditButton type="button" onClick={() => history.push(editPath)}>
            <SettingsOutlinedIcon /> {t('profileSidebar.manageProfile')}
          </EditButton>
        )}
      </Body>
    </Aside>
  );
};

const Aside = styled.aside`
  width: 100%;
  min-width: 0;
  max-width: 18rem;
  position: sticky;
  top: 5.7rem;
  overflow: hidden;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-xl);
  background: #fff;
  box-shadow: var(--shadow-sm);
  align-self: start;
`;

const Cover = styled.div`
  height: 6rem;
  display: grid;
  place-items: center end;
  padding: 1rem;
  background:
    linear-gradient(135deg, #000 0%, #171717 68%, #c0a375 180%);

  img { opacity: .2; }
`;

const Body = styled.div`
  min-width: 0;
  padding: 0 1rem 1.05rem;
  text-align: center;

  > * { min-width: 0; }
`;

const AvatarWrap = styled.div`
  width: 6.4rem;
  height: 6.4rem;
  position: relative;
  margin: -3.2rem auto .75rem;
  padding: .25rem;
  border-radius: 1.65rem;
  background: #fff;
  box-shadow: 0 .85rem 2rem rgba(0,0,0,.17);
`;

const Avatar = styled(FirebaseImage)`
  width: 100%;
  height: 100%;
  border-radius: 1.4rem;
  object-fit: cover;
  background: #f1ece5;
`;

const Name = styled.h2`
  margin: 0;
  color: #111;
  font-size: 1.08rem;
  line-height: 1.35;
  overflow-wrap: break-word;
`;

const Role = styled.div`
  margin-top: .25rem;
  color: var(--color-muted);
  font-size: .8rem;
`;

const UploadLabel = styled.label`
  position: absolute;
  right: -.35rem;
  bottom: -.35rem;
  width: 2.25rem;
  height: 2.25rem;
  display: grid;
  place-items: center;
  border: 3px solid #fff;
  border-radius: 50%;
  color: #000;
  background: #c0a375;
  cursor: pointer;
  box-shadow: 0 .5rem 1rem rgba(0,0,0,.18);

  input { position: absolute; width: 1px; height: 1px; opacity: 0; pointer-events: none; }
  svg { font-size: 1rem; }
  &[aria-disabled='true'] { opacity: .55; cursor: wait; }
`;

const Progress = styled.div`
  margin-top: .55rem;
  color: var(--color-accentDark);
  font-size: .72rem;
  font-weight: 750;
`;

const RatingCard = styled.div`
  margin-top: 1rem;
  padding: .85rem;
  border-radius: 1rem;
  background: #f8f3eb;
`;

const RatingLabel = styled.div`
  margin-bottom: .35rem;
  color: #715418;
  font-size: .7rem;
  font-weight: 900;
  letter-spacing: .06em;
  text-transform: uppercase;
`;

const RatingLine = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: .5rem;
`;

const Muted = styled.div`color: #776b54; font-size: .8rem;`;
const Details = styled.div`width: 100%; min-width: 0; display: grid; gap: .5rem; margin-top: 1rem; text-align: left;`;
const detailStyles = `
  width: 100%; display: grid; gap: .14rem; padding: .72rem .8rem; border: 1px solid #e8e4de;
  border-radius: .85rem; background: #faf9f7; color: #111; text-align: left;
  span { color: #77726b; font-size: .67rem; font-weight: 800; letter-spacing: .05em; text-transform: uppercase; }
  strong { font-size: .84rem; overflow-wrap: anywhere; }
`;
const Detail = styled.div`${detailStyles}`;
const DetailButton = styled.button`${detailStyles} font: inherit; cursor: pointer; &:hover { border-color: #c0a375; background: #fbf7f1; }`;
const EditButton = styled.button`
  width: 100%;
  min-height: 2.8rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: .5rem;
  margin-top: 1rem;
  padding: .65rem .85rem;
  border: 1px solid #000;
  border-radius: 999px;
  background: #000;
  color: #fff;
  font: inherit;
  font-size: .8rem;
  font-weight: 800;
  cursor: pointer;

  &:hover { background: #171717; }
  svg { font-size: 1.05rem; }
`;

export default ProfileSidebar;
